import { useState, useMemo, useEffect } from 'react';
import { formatINR } from '@/utils/format';
import { complianceApi, type TDSEntryRecord, type GstReturnFiling, type ChallanGroupRecord, type Gstr2xReconciliationResult } from '@/api/compliance.api';
import OtpAuthModal from './OtpAuthModal';
import ConfirmDialog from '@/components/feature/ConfirmDialog';
import { useToast } from '@/contexts/ToastContext';

function formatDate(d: string | null | undefined): string {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return d; }
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/**
 * Calculates Indian Financial Year (Apr-Mar) and Quarter for a given date
 */
export function getIndianFYAndQuarter(dateStrOrObj: string | Date): { quarter: string; fy: string; prefix: string } {
  const d = new Date(dateStrOrObj);
  if (isNaN(d.getTime())) return { quarter: 'Q1', fy: '2026-27', prefix: 'Q1-2026-27' };

  const m = d.getMonth() + 1; // 1 = Jan, ..., 8 = Aug, ..., 12 = Dec
  const y = d.getFullYear(); // e.g. 2026

  let q = 'Q1';
  let fyStartYear = y;

  if (m >= 4 && m <= 6) {
    q = 'Q1';
    fyStartYear = y;
  } else if (m >= 7 && m <= 9) {
    q = 'Q2';
    fyStartYear = y;
  } else if (m >= 10 && m <= 12) {
    q = 'Q3';
    fyStartYear = y;
  } else {
    // Jan, Feb, Mar belong to Q4 of previous calendar year's FY
    q = 'Q4';
    fyStartYear = y - 1;
  }

  const fy = `${fyStartYear}-${String(fyStartYear + 1).slice(2)}`;
  return { quarter: q, fy, prefix: `${q}-${fy}` };
}

interface QuarterlyReturnPageProps {
  entries?: TDSEntryRecord[];
  onClose: () => void;
}

export default function QuarterlyReturnPage({ entries, onClose }: QuarterlyReturnPageProps) {
  const toast = useToast();
  const now = new Date();
  const currentFYInfo = getIndianFYAndQuarter(now);

  const [quarter, setQuarter] = useState<string>(currentFYInfo.quarter);
  const [fy, setFy] = useState<string>(currentFYInfo.fy);
  const [formType, setFormType] = useState<'26Q' | '27EQ'>('27EQ'); // 26Q (TDS) or 27EQ (TCS)
  const activeEntries = entries || [];

  // Available FY options (past 2 years, current year, next year)
  const fyOptions = useMemo(() => {
    const currentStart = parseInt(currentFYInfo.fy.split('-')[0], 10);
    return [
      `${currentStart - 2}-${String(currentStart - 1).slice(2)}`,
      `${currentStart - 1}-${String(currentStart).slice(2)}`,
      `${currentStart}-${String(currentStart + 1).slice(2)}`,
      `${currentStart + 1}-${String(currentStart + 2).slice(2)}`,
    ];
  }, [currentFYInfo.fy]);

  // ─── GSTR7/GSTR8 monthly filing with GSTN (separate from the quarterly
  // 26Q/27EQ Income Tax summary below — GST TDS/TCS under Section 51/52 CGST
  // is filed monthly with GSTN via GSTR7/GSTR8, not quarterly with TRACES) ──
  const [filingType, setFilingType] = useState<'GSTR7' | 'GSTR8'>('GSTR8');
  const [filingMonth, setFilingMonth] = useState(now.getMonth() + 1);
  const [filingYear, setFilingYear] = useState(now.getFullYear());
  const retPeriod = `${String(filingMonth).padStart(2, '0')}${filingYear}`;
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [filing, setFiling] = useState<GstReturnFiling | null>(null);
  const [gstnStatus, setGstnStatus] = useState<string | null>(null);
  const [savingReturn, setSavingReturn] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [filingReturn, setFilingReturn] = useState(false);
  const [showFileConfirm, setShowFileConfirm] = useState(false);

  const loadFilingStatus = async () => {
    try {
      const res = await complianceApi.getGstFilings(filingType, retPeriod);
      if (res?.success) setFiling((res.data as GstReturnFiling) || null);
    } catch (err) {
      console.error('Failed to load filing status:', err);
    }
  };

  useEffect(() => { loadFilingStatus(); }, [filingType, retPeriod]);

  // Prefiling check — GSTR7 shouldn't be filed before that month's vendor TDS
  // (194-series) was deposited via challan, and GSTR8 needs the equivalent TCS
  // (206C) challan deposited (Stage 5) — same ITNS 281 form, different section.
  const [periodChallan, setPeriodChallan] = useState<ChallanGroupRecord | null>(null);
  useEffect(() => {
    let mounted = true;
    const challanType = filingType === 'GSTR7' ? 'TDS' : 'TCS';
    complianceApi.getChallanGroups(challanType).then((res) => {
      if (!mounted || !res?.success) return;
      const match = (res.data || []).find((g) => g.periodMonth === filingMonth && g.periodYear === filingYear);
      setPeriodChallan(match || null);
    }).catch(() => {});
    return () => { mounted = false; };
  }, [filingType, filingMonth, filingYear]);

  const handleSaveReturn = async () => {
    setSavingReturn(true);
    try {
      const res = filingType === 'GSTR7'
        ? await complianceApi.saveGSTR7(retPeriod, fy)
        : await complianceApi.saveGSTR8(retPeriod, fy);
      if (res?.success) {
        toast.success(res.message || `${filingType} saved to GSTN ✓`);
        await loadFilingStatus();
      } else if (res?.authRequired) {
        toast.error(res.message || 'Authenticate with GSTN first');
        setShowOtpModal(true);
      } else {
        toast.error(res?.message || `Failed to save ${filingType}`);
      }
    } catch (err: any) {
      toast.error(err?.message || `Failed to save ${filingType}`);
    } finally {
      setSavingReturn(false);
    }
  };

  const handleCheckStatus = async () => {
    setCheckingStatus(true);
    try {
      const res = filingType === 'GSTR7' ? await complianceApi.getGSTR7Status(retPeriod) : await complianceApi.getGSTR8Status(retPeriod);
      if (res?.success) {
        setGstnStatus(res.data?.mappedStatus || res.data?.gstnStatus || null);
        toast.success(`GSTN status: ${res.data?.mappedStatus || 'unknown'}`);
        await loadFilingStatus();
      } else if (res?.authRequired) {
        toast.error(res.message || 'Authenticate with GSTN first');
        setShowOtpModal(true);
      } else {
        toast.error(res?.message || 'Failed to check status');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to check status');
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleFileReturn = async () => {
    setShowFileConfirm(false);
    setFilingReturn(true);
    try {
      const res = filingType === 'GSTR7' ? await complianceApi.fileGSTR7(retPeriod) : await complianceApi.fileGSTR8(retPeriod);
      if (res?.success) {
        toast.success(res.message || `${filingType} filed ✓`);
        await loadFilingStatus();
      } else if (res?.authRequired) {
        toast.error(res.message || 'Authenticate with GSTN first');
        setShowOtpModal(true);
      } else {
        toast.error(res?.message || `Failed to file ${filingType}`);
      }
    } catch (err: any) {
      toast.error(err?.message || `Failed to file ${filingType}`);
    } finally {
      setFilingReturn(false);
    }
  };

  // ─── GSTR2X Reconciliation — match GSTN's TDS-credit-available statement
  // against our own TDS_RECEIVABLE entries, one month at a time across the quarter ──
  const [reconciling, setReconciling] = useState(false);
  const [reconcileResult, setReconcileResult] = useState<Gstr2xReconciliationResult | null>(null);
  const [lastReconciledAt, setLastReconciledAt] = useState<string | null>(null);

  const monthsInQuarter = (q: string, financialYear: string): { month: number; year: number }[] => {
    const fyStart = parseInt(financialYear.split('-')[0], 10);
    const starts: Record<string, number> = { Q1: 4, Q2: 7, Q3: 10, Q4: 1 };
    const startMonth = starts[q] ?? 4;
    const yearOffset = q === 'Q4' ? 1 : 0;
    return [0, 1, 2].map((i) => ({ month: startMonth + i, year: fyStart + yearOffset }));
  };

  const handleReconcile = async () => {
    setReconciling(true);
    try {
      const periods = monthsInQuarter(quarter, fy);
      const merged: Gstr2xReconciliationResult = {
        matched: [], mismatched: [], missing: [],
        totalEntries: 0, totalGovtCredits: 0, totalGSTNAmount: 0, totalOurAmount: 0,
        reconciledAt: new Date().toISOString(),
      };
      for (const p of periods) {
        const rPeriod = `${String(p.month).padStart(2, '0')}${p.year}`;
        const res = await complianceApi.reconcileGSTR2X(rPeriod);
        if (res?.authRequired) {
          toast.error(res.message || 'Authenticate with GSTN first');
          setShowOtpModal(true);
          return;
        }
        if (!res?.success || !res.data) {
          toast.error(res?.message || `Failed to reconcile ${rPeriod}`);
          continue;
        }
        merged.matched.push(...res.data.matched);
        merged.mismatched.push(...res.data.mismatched);
        merged.missing.push(...res.data.missing);
        merged.totalEntries += res.data.totalEntries;
        merged.totalGovtCredits += res.data.totalGovtCredits;
        merged.totalGSTNAmount += res.data.totalGSTNAmount;
        merged.totalOurAmount += res.data.totalOurAmount;
      }
      setReconcileResult(merged);
      setLastReconciledAt(merged.reconciledAt);
      toast.success(`Reconciled ${quarter} ${fy} — ${merged.matched.length} matched, ${merged.mismatched.length} mismatch, ${merged.missing.length} missing`);
    } catch (err: any) {
      toast.error(err?.message || 'Reconciliation failed');
    } finally {
      setReconciling(false);
    }
  };

  // Filter entries for selected quarter & FY
  const quarterEntries = useMemo(() => {
    const targetPrefix = `${quarter}-${fy}`.toUpperCase();
    return activeEntries.filter(e => {
      // 1. Match explicit quarter and financialYear
      if (e.quarter && e.financialYear) {
        if (e.quarter.toUpperCase() === quarter.toUpperCase() && e.financialYear === fy) {
          return true;
        }
      }

      // 2. Match quarterlyReturn field (e.g. 'Q2-2026-27')
      if (e.quarterlyReturn && e.quarterlyReturn.toUpperCase() === targetPrefix) {
        return true;
      }

      // 3. Fallback to date calculation
      const targetDate = e.paymentDate || e.depositDate || e.createdAt;
      if (targetDate) {
        const calculated = getIndianFYAndQuarter(targetDate);
        if (calculated.quarter === quarter && calculated.fy === fy) {
          return true;
        }
      }

      return false;
    });
  }, [activeEntries, quarter, fy]);

  const isTCS = formType === '27EQ';

  // Filter entries according to active form (26Q TDS vs 27EQ TCS)
  const activeFormEntries = useMemo(() => {
    return quarterEntries.filter(e => {
      if (isTCS) {
        return (
          e.type === 'TCS' ||
          (e.type as string) === 'TCS_COLLECTED' ||
          (e.type as string) === 'TCS_PAYABLE' ||
          (e.section && e.section.startsWith('206C'))
        );
      } else {
        return (
          e.type === 'TDS' ||
          (e.type as string) === 'TDS_RECEIVABLE' ||
          (e.type as string) === 'TDS_PAYABLE' ||
          (e.section && e.section.startsWith('194'))
        );
      }
    });
  }, [quarterEntries, isTCS]);

  // ─── Validation Checks ───────────────────────────────────────────
  const checks = useMemo(() => {
    const results: { label: string; pass: boolean }[] = [];
    const entriesToCheck = activeFormEntries;

    results.push({
      label: isTCS ? 'All collectees have PAN' : 'All deductees have PAN',
      pass: entriesToCheck.length === 0 || entriesToCheck.every(e => e.partyPAN && e.partyPAN !== '—' && e.partyPAN.trim().length === 10),
    });

    const deposited = entriesToCheck.filter(e => e.status === 'DEPOSITED');
    results.push({
      label: `${isTCS ? 'TCS' : 'TDS'} deposited before 7th of following month`,
      pass: deposited.length > 0 ? deposited.every(e => {
        if (!e.depositDate) return true;
        const paymentDate = new Date(e.paymentDate || e.createdAt);
        const deadline = new Date(paymentDate.getFullYear(), paymentDate.getMonth() + 1, 7);
        return new Date(e.depositDate) <= deadline;
      }) : true,
    });

    const withoutChallan = entriesToCheck.filter(e => e.status === 'DEPOSITED' && !e.tdsChallanNumber);
    results.push({
      label: 'All deposited entries have challan number',
      pass: withoutChallan.length === 0,
    });

    const missingPan = entriesToCheck.filter(e => !e.partyPAN || e.partyPAN === '—' || e.partyPAN.trim().length !== 10);
    results.push({
      label: 'No missing PAN entries',
      pass: missingPan.length === 0,
    });

    return results;
  }, [activeFormEntries, isTCS]);

  // ─── Totals ──────────────────────────────────────────────────────
  const totalAmount = activeFormEntries.reduce((s, e) => s + (e.paymentAmount || e.taxableAmount || 0), 0);
  const totalTax = activeFormEntries.reduce((s, e) => s + (e.tdsAmount || 0), 0);
  const totalDeposited = activeFormEntries.filter(e => e.status === 'DEPOSITED').reduce((s, e) => s + (e.tdsAmount || 0), 0);

  // ─── Status badge for row ────────────────────────────────────────
  const rowStatusClass = (entry: TDSEntryRecord) => {
    if (entry.status === 'DEPOSITED') return 'bg-green-50/50';
    if (entry.status === 'DEDUCTED') return 'bg-amber-50/50';
    if (entry.status === 'PENDING') return 'bg-red-50/50';
    return '';
  };

  // ─── CSV / Excel Download ────────────────────────────────────────────────
  const handleDownloadCSV = () => {
    const headers = isTCS
      ? ['Buyer Name', 'PAN', 'Section', 'Sales Amount', 'TCS Collected', 'TCS Deposited', 'Challan Ref', 'Status']
      : ['Party Name', 'PAN', 'Section', 'Payment Amt', 'TDS Deducted', 'TDS Deposited', 'Challan Ref', 'Status'];

    const rows = activeFormEntries.map(e => [
      `"${e.partyName || ''}"`,
      e.partyPAN || '',
      e.section || '',
      (e.paymentAmount || e.taxableAmount || 0).toString(),
      (e.tdsAmount || 0).toString(),
      e.status === 'DEPOSITED' ? (e.tdsAmount || 0).toString() : '0',
      e.tdsChallanNumber ?? '',
      e.status,
    ]);

    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${isTCS ? 'TCS_27EQ' : 'TDS_26Q'}_${quarter}_${fy.replace('-', '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-[#f8fafc]">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-white border-b border-[#e2e8f0] shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#f1f5f9] text-slate-500 cursor-pointer">
            <i className="ri-arrow-left-line" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-[#1e293b]">Quarterly TDS/TCS Return</h1>
            <p className="text-xs text-slate-400">Form 26Q / 27EQ — {isTCS ? 'Collectee-wise summary (TCS)' : 'Deductee-wise summary (TDS)'}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={handleDownloadCSV}
            className="flex items-center gap-2 h-9 px-4 rounded-lg border border-[#e2e8f0] bg-white text-sm font-medium text-slate-700 hover:bg-[#f8fafc] cursor-pointer whitespace-nowrap transition-colors">
            <i className="ri-file-excel-2-line" /> Download as Excel
          </button>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-4 px-6 py-4 bg-white border-b border-[#e2e8f0] shrink-0 flex-wrap">
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">Quarter</label>
          <select value={quarter} onChange={e => setQuarter(e.target.value)}
            className="h-9 px-3 rounded-lg border border-[#e2e8f0] text-sm bg-white focus:outline-none focus:border-[#4f46e5] font-medium">
            <option value="Q1">Q1 (Apr-Jun)</option>
            <option value="Q2">Q2 (Jul-Sep)</option>
            <option value="Q3">Q3 (Oct-Dec)</option>
            <option value="Q4">Q4 (Jan-Mar)</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">Financial Year</label>
          <select value={fy} onChange={e => setFy(e.target.value)}
            className="h-9 px-3 rounded-lg border border-[#e2e8f0] text-sm bg-white focus:outline-none focus:border-[#4f46e5] font-medium">
            {fyOptions.map(f => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 block mb-1">Form</label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setFormType('26Q');
                setFilingType('GSTR7');
              }}
              className={`h-9 px-4 rounded-lg text-sm font-medium transition-colors cursor-pointer border ${
                formType === '26Q'
                  ? 'bg-[#4f46e5] text-white border-[#4f46e5] shadow-sm'
                  : 'bg-white text-slate-600 border-[#e2e8f0] hover:bg-[#f8fafc]'
              }`}
            >
              26Q (TDS)
            </button>
            <button
              type="button"
              onClick={() => {
                setFormType('27EQ');
                setFilingType('GSTR8');
              }}
              className={`h-9 px-4 rounded-lg text-sm font-medium transition-colors cursor-pointer border ${
                formType === '27EQ'
                  ? 'bg-[#4f46e5] text-white border-[#4f46e5] shadow-sm'
                  : 'bg-white text-slate-600 border-[#e2e8f0] hover:bg-[#f8fafc]'
              }`}
            >
              27EQ (TCS)
            </button>
          </div>
        </div>
        <div className="ml-auto text-sm text-slate-500 font-medium">
          {activeFormEntries.length} entries · {formatINR(totalTax)} total {isTCS ? 'TCS' : 'TDS'}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
        {/* File GSTR7/GSTR8 with GSTN — monthly, via Whitebooks */}
        <div>
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">
            File with GSTN (Whitebooks) — GSTR7/GSTR8 are monthly, separate from the quarterly 26Q/27EQ summary below
          </h3>
          <div className="bg-white border border-[#e2e8f0] rounded-xl p-5 space-y-4 shadow-sm">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setFilingType('GSTR7');
                    setFormType('26Q');
                  }}
                  className={`px-4 py-1.5 rounded-lg text-xs font-semibold border cursor-pointer whitespace-nowrap transition-colors ${
                    filingType === 'GSTR7'
                      ? 'bg-[#4f46e5] text-white border-[#4f46e5]'
                      : 'bg-white text-slate-500 border-[#e2e8f0] hover:border-[#4f46e5]'
                  }`}
                >
                  GSTR7 (TDS)
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setFilingType('GSTR8');
                    setFormType('27EQ');
                  }}
                  className={`px-4 py-1.5 rounded-lg text-xs font-semibold border cursor-pointer whitespace-nowrap transition-colors ${
                    filingType === 'GSTR8'
                      ? 'bg-[#4f46e5] text-white border-[#4f46e5]'
                      : 'bg-white text-slate-500 border-[#e2e8f0] hover:border-[#4f46e5]'
                  }`}
                >
                  GSTR8 (TCS)
                </button>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Month</label>
                <select value={filingMonth} onChange={e => setFilingMonth(Number(e.target.value))}
                  className="h-9 px-3 rounded-lg border border-[#e2e8f0] text-sm bg-white focus:outline-none focus:border-[#4f46e5] font-medium">
                  {MONTH_NAMES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Year</label>
                <select value={filingYear} onChange={e => setFilingYear(Number(e.target.value))}
                  className="h-9 px-3 rounded-lg border border-[#e2e8f0] text-sm bg-white focus:outline-none focus:border-[#4f46e5] font-medium">
                  {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              {filing && (
                <span className={`ml-auto px-2.5 py-1 rounded-full text-xs font-semibold ${
                  filing.status === 'FILED' ? 'bg-green-100 text-green-700' :
                  filing.status === 'ERROR' ? 'bg-red-100 text-red-700' :
                  'bg-blue-100 text-blue-700'
                }`}>
                  {filing.status}{filing.arn ? ` · ARN ${filing.arn}` : ''}
                </span>
              )}
            </div>

            {filing?.status === 'ERROR' && filing.error_details && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">{filing.error_details}</div>
            )}

            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs ${periodChallan ? 'bg-green-50 border-green-200 text-green-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
              <i className={periodChallan ? 'ri-checkbox-circle-fill' : 'ri-alert-line'} />
              {periodChallan
                ? `${filingType === 'GSTR7' ? 'TDS' : 'TCS'} Challan deposited (${formatINR(periodChallan.totalPayable ?? periodChallan.totalAmount)} on ${formatDate(periodChallan.depositDate)}, no. ${periodChallan.challanNumber})`
                : `${filingType === 'GSTR7' ? 'TDS' : 'TCS'} Challan not yet deposited for ${MONTH_NAMES[filingMonth - 1]} ${filingYear} — deposit via Challan Register before filing ${filingType}`}
            </div>

            <div className="flex items-center gap-3 flex-wrap pt-2 border-t border-[#f1f5f9]">
              <button onClick={() => setShowOtpModal(true)}
                className="flex items-center gap-2 h-9 px-4 rounded-lg border border-emerald-300 text-sm font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 cursor-pointer whitespace-nowrap transition-colors">
                <i className="ri-shield-keyhole-line" /> Authenticate with GSTN
              </button>
              <button onClick={handleSaveReturn} disabled={savingReturn || filing?.status === 'FILED'}
                className="flex items-center gap-2 h-9 px-4 rounded-lg border border-[#4f46e5] text-sm font-medium text-[#4f46e5] hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap transition-colors">
                {savingReturn ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-save-line" />}
                Save to GSTN
              </button>
              <button onClick={handleCheckStatus} disabled={checkingStatus || !filing?.ref_id}
                className="flex items-center gap-2 h-9 px-4 rounded-lg border border-[#e2e8f0] text-sm font-medium text-slate-600 hover:bg-[#f8fafc] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap transition-colors">
                {checkingStatus ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-refresh-line" />}
                Check Status
              </button>
              <button onClick={() => setShowFileConfirm(true)} disabled={filingReturn || !filing?.ref_id || filing?.status === 'FILED'}
                className="flex items-center gap-2 h-9 px-5 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap transition-colors">
                {filingReturn ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-send-plane-fill" />}
                File {filingType} (Irreversible)
              </button>
            </div>
            <p className="text-xs text-slate-400">
              Save first, then File — filing submits officially to GSTN and cannot be undone.
            </p>
          </div>
        </div>

        {/* GSTR2X Reconciliation — match GSTN's TDS-credit-available statement against
            our own TDS Receivable entries (Stage 3/4), one month at a time across the quarter */}
        <div>
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">
            GSTR2X Reconciliation — match GSTN TDS/TCS credits with our records
          </h3>
          <div className="bg-white border border-[#e2e8f0] rounded-xl p-5 space-y-4 shadow-sm">
            <div className="flex items-center gap-4 flex-wrap">
              <p className="text-xs text-slate-500">
                Period: <span className="font-semibold text-[#1e293b]">{quarter} {fy}</span> (uses the Quarter/FY selected above)
              </p>
              <button onClick={handleReconcile} disabled={reconciling}
                className="ml-auto flex items-center gap-2 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap transition-colors">
                {reconciling ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-refresh-line" />}
                Fetch from GSTN &amp; Reconcile
              </button>
            </div>
            <p className="text-xs text-slate-400">
              Last reconciled: {lastReconciledAt ? new Date(lastReconciledAt).toLocaleString('en-IN') : 'Never'}
            </p>

            {reconcileResult && (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-lg p-3">
                    <p className="text-[10px] text-slate-400 uppercase tracking-wide">TDS Credits Available (GSTN)</p>
                    <p className="text-base font-bold text-[#1e293b]">{formatINR(reconcileResult.totalGSTNAmount)}</p>
                  </div>
                  <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-lg p-3">
                    <p className="text-[10px] text-slate-400 uppercase tracking-wide">TDS in Our Records</p>
                    <p className="text-base font-bold text-[#1e293b]">{formatINR(reconcileResult.totalOurAmount)}</p>
                  </div>
                  <div className={`rounded-lg p-3 border ${Math.abs(reconcileResult.totalGSTNAmount - reconcileResult.totalOurAmount) <= 1 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                    <p className="text-[10px] text-slate-400 uppercase tracking-wide">Difference</p>
                    <p className={`text-base font-bold ${Math.abs(reconcileResult.totalGSTNAmount - reconcileResult.totalOurAmount) <= 1 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatINR(Math.abs(reconcileResult.totalGSTNAmount - reconcileResult.totalOurAmount))}
                      {Math.abs(reconcileResult.totalGSTNAmount - reconcileResult.totalOurAmount) <= 1 ? ' ✅' : ' ⚠️'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 text-xs flex-wrap">
                  <span className="px-2.5 py-1 rounded-full bg-green-100 text-green-700 font-semibold">✅ MATCHED: {reconcileResult.matched.length} ({formatINR(reconcileResult.matched.reduce((s, e) => s + (e.gstnAmount ?? e.tdsAmount), 0))})</span>
                  <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 font-semibold">⚠️ MISMATCH: {reconcileResult.mismatched.length} ({formatINR(reconcileResult.mismatched.reduce((s, e) => s + Math.abs(e.difference || 0), 0))})</span>
                  <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 font-semibold">❓ MISSING FROM GSTN: {reconcileResult.missing.length}</span>
                </div>

                <div className="border border-[#e2e8f0] rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-slate-500 border-b border-[#e2e8f0] bg-[#f8fafc]">
                        <th className="py-2.5 px-3 font-medium">Customer</th>
                        <th className="py-2.5 px-3 font-medium">Section</th>
                        <th className="py-2.5 px-3 font-medium text-right">Our Amt</th>
                        <th className="py-2.5 px-3 font-medium text-right">GSTN Amt</th>
                        <th className="py-2.5 px-3 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...reconcileResult.matched, ...reconcileResult.mismatched, ...reconcileResult.missing].length === 0 ? (
                        <tr><td colSpan={5} className="py-8 text-center text-slate-400 text-sm">No entries for this quarter</td></tr>
                      ) : (
                        <>
                          {reconcileResult.matched.map((e) => (
                            <tr key={`m-${e.id}`} className="border-b border-[#f1f5f9]">
                              <td className="py-2.5 px-3 text-[#1e293b]">{e.partyName}</td>
                              <td className="py-2.5 px-3 text-slate-500">{e.section}</td>
                              <td className="py-2.5 px-3 text-right">{formatINR(e.tdsAmount)}</td>
                              <td className="py-2.5 px-3 text-right">{formatINR(e.gstnAmount ?? e.tdsAmount)}</td>
                              <td className="py-2.5 px-3"><span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">✅ MATCH</span></td>
                            </tr>
                          ))}
                          {reconcileResult.mismatched.map((e) => (
                            <tr key={`x-${e.id}`} className="border-b border-[#f1f5f9] bg-amber-50/40">
                              <td className="py-2.5 px-3 text-[#1e293b]">{e.partyName}</td>
                              <td className="py-2.5 px-3 text-slate-500">{e.section}</td>
                              <td className="py-2.5 px-3 text-right">{formatINR(e.tdsAmount)}</td>
                              <td className="py-2.5 px-3 text-right">{formatINR(e.gstnAmount ?? 0)}</td>
                              <td className="py-2.5 px-3">
                                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium" title={e.note}>
                                  ⚠️ MISMATCH (₹{e.difference})
                                </span>
                              </td>
                            </tr>
                          ))}
                          {reconcileResult.missing.map((e) => (
                            <tr key={`n-${e.id}`} className="border-b border-[#f1f5f9] bg-slate-50/60">
                              <td className="py-2.5 px-3 text-[#1e293b]">{e.partyName}</td>
                              <td className="py-2.5 px-3 text-slate-500">{e.section}</td>
                              <td className="py-2.5 px-3 text-right">{formatINR(e.tdsAmount)}</td>
                              <td className="py-2.5 px-3 text-right text-slate-400">—</td>
                              <td className="py-2.5 px-3"><span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">🔍 NOT IN GSTN YET</span></td>
                            </tr>
                          ))}
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
                {reconcileResult.missing.length > 0 && (
                  <p className="text-xs text-slate-400">
                    Not found in GSTN yet — the customer may not have filed their GSTR7 for this period. TDS credit isn't claimable in your ITR until it appears here.
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        {/* Validation Checks */}
        <div>
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Validation Checks ({isTCS ? '27EQ TCS' : '26Q TDS'})</h3>
          <div className="space-y-2">
            {checks.map((check, i) => (
              <div key={i} className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm ${check.pass ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'
                }`}>
                <i className={check.pass ? 'ri-checkbox-circle-fill text-green-500' : 'ri-close-circle-fill text-red-500'} />
                {check.label}
              </div>
            ))}
          </div>
        </div>

        {/* Deductee / Collectee-wise Summary Table */}
        <div>
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">
            {isTCS ? 'Collectee-wise Summary (27EQ TCS)' : 'Deductee-wise Summary (26Q TDS)'} — {quarter} {fy}
          </h3>
          <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden shadow-sm">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-[#e2e8f0] bg-[#f8fafc]">
                  <th className="py-3 px-4 font-medium">{isTCS ? 'Buyer Name' : 'Party Name'}</th>
                  <th className="py-3 px-4 font-medium">PAN</th>
                  <th className="py-3 px-4 font-medium">Section</th>
                  <th className="py-3 px-4 font-medium text-right">{isTCS ? 'Sales Amount' : 'Payment Amt'}</th>
                  <th className="py-3 px-4 font-medium text-right">{isTCS ? 'TCS Collected' : 'TDS Deducted'}</th>
                  <th className="py-3 px-4 font-medium text-right">{isTCS ? 'TCS Deposited' : 'TDS Deposited'}</th>
                  <th className="py-3 px-4 font-medium">Challan Ref</th>
                  <th className="py-3 px-4 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {activeFormEntries.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-12 text-center text-slate-400">
                      <i className="ri-inbox-line text-3xl block mb-2" />
                      <p className="text-sm font-medium">No {isTCS ? 'TCS (27EQ)' : 'TDS (26Q)'} entries found for {quarter} {fy}</p>
                      <p className="text-xs text-slate-400 mt-1">Make sure the correct Quarter and Financial Year are selected above</p>
                    </td>
                  </tr>
                ) : (
                  activeFormEntries.map(e => (
                    <tr key={e.id} className={`border-b border-[#f1f5f9] hover:bg-slate-50/50 transition-colors ${rowStatusClass(e)}`}>
                      <td className="py-3 px-4 font-medium text-[#1e293b]">{e.partyName}</td>
                      <td className="py-3 px-4 font-mono text-xs text-slate-600 font-semibold">{e.partyPAN || '—'}</td>
                      <td className="py-3 px-4 text-xs font-semibold text-indigo-600">
                        <span className="px-2 py-0.5 rounded bg-indigo-50 border border-indigo-100">{e.section}</span>
                      </td>
                      <td className="py-3 px-4 text-right font-medium text-[#1e293b]">
                        {formatINR(e.paymentAmount || e.taxableAmount || 0)}
                      </td>
                      <td className="py-3 px-4 text-right font-bold text-red-600">
                        {formatINR(e.tdsAmount || 0)}
                      </td>
                      <td className="py-3 px-4 text-right font-bold text-green-600">
                        {e.status === 'DEPOSITED' ? formatINR(e.tdsAmount || 0) : '—'}
                      </td>
                      <td className="py-3 px-4 font-mono text-xs text-slate-500">{e.tdsChallanNumber || '—'}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${e.status === 'DEPOSITED' ? 'bg-green-100 text-green-700 border-green-200' :
                            e.status === 'DEDUCTED' ? 'bg-amber-100 text-amber-700 border-amber-200' :
                              e.status === 'PENDING' ? 'bg-red-100 text-red-700 border-red-200' :
                                'bg-gray-100 text-gray-500 border-gray-200'
                          }`}>
                          {e.status === 'DEPOSITED' ? 'Deposited' : e.status === 'DEDUCTED' ? (isTCS ? 'Collected' : 'Deducted') : e.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-[#e2e8f0] bg-[#f8fafc] font-semibold">
                  <td className="py-3 px-4 text-[#1e293b]" colSpan={3}>Total</td>
                  <td className="py-3 px-4 text-right text-[#1e293b]">{formatINR(totalAmount)}</td>
                  <td className="py-3 px-4 text-right text-red-600">{formatINR(totalTax)}</td>
                  <td className="py-3 px-4 text-right text-green-600">{formatINR(totalDeposited)}</td>
                  <td className="py-3 px-4" colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      {showOtpModal && (
        <OtpAuthModal onClose={() => setShowOtpModal(false)} onAuthenticated={() => toast.success('Authenticated with GSTN ✓')} />
      )}

      <ConfirmDialog
        open={showFileConfirm}
        title={`File ${filingType} with GSTN?`}
        message={`This officially submits your ${filingType} return for ${MONTH_NAMES[filingMonth - 1]} ${filingYear} to GSTN and cannot be undone.`}
        confirmLabel="Yes, File It"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={handleFileReturn}
        onCancel={() => setShowFileConfirm(false)}
      />
    </div>
  );
}